import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import {
  Brain,
  CheckCircle2,
  Dumbbell,
  MapPin,
  Shield,
  Star,
  Target,
} from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";

const benefits = [
  {
    icon: Shield,
    title: "Confidence Under Pressure",
    desc: "Learn to stay composed and perform when it counts most.",
  },
  {
    icon: Target,
    title: "Discipline and Accountability",
    desc: "Develop the habits and mindset needed to show up, work hard, and improve.",
  },
  {
    icon: Brain,
    title: "Focus and Mental Resilience",
    desc: "Training sharpens the mind. Teens learn to stay focused when things get difficult.",
  },
  {
    icon: Dumbbell,
    title: "Physical Fitness",
    desc: "Build strength, conditioning, and athleticism through purposeful, engaging training.",
  },
];

const trainingSteps = [
  {
    number: "01",
    title: "Fundamentals First",
    desc: "Every teen starts with a strong foundation — positions, escapes, and core techniques explained clearly.",
  },
  {
    number: "02",
    title: "Coached Drilling",
    desc: "Repetition with purpose. Teens drill under instructor guidance to build muscle memory and technique.",
  },
  {
    number: "03",
    title: "Live Training",
    desc: "Supervised sparring develops timing, problem-solving, and real-world confidence on the mat.",
  },
];

const audiences = [
  {
    icon: Star,
    title: "Complete Beginners",
    desc: "No experience needed. Teens start from the basics and build from day one.",
  },
  {
    icon: Dumbbell,
    title: "Athletes and Competitors",
    desc: "Take your athletic development to the next level with technical, high-intensity training.",
  },
  {
    icon: CheckCircle2,
    title: "Teens Who Need Structure",
    desc: "Teens who thrive with clear goals, accountability, and a positive training environment.",
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

export default function TeensJiuJitsuPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Teens Jiu Jitsu Classes in Springfield, OR | Superhero Jiu Jitsu Academy
      </title>
      <meta
        name="description"
        content="Enroll your teenager in No-Gi Jiu-Jitsu at Superhero Jiu Jitsu Academy in Springfield, OR. Each session helps young adults build confidence, discipline, and mental toughness through structured training."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="teens-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/assets/images/teen jiu-jitsu hero image.jpg')",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 40%, oklch(0.68 0.16 82) 0%, transparent 55%), radial-gradient(circle at 75% 70%, oklch(0.68 0.16 82 / 0.35) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, oklch(0.12 0.05 255 / 0.9) 0%, oklch(0.12 0.05 255 / 0.55) 55%, oklch(0.12 0.05 255 / 0.2) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                Ages 11–16 · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                Teen Jiu Jitsu in{" "}
                <span className="text-gold">Springfield, OR</span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-1">
                  (Ages 11–16)
                </span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Build confidence, discipline, and mental toughness through
                structured training.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Focused Training",
                  "No Experience Needed",
                  "Expert Coaching",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/40 text-sm font-body font-medium text-hero-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <div
                className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
                data-ocid="teens-hero-form"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
                <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                  Book a Free Class
                </h2>
                <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                  First class is on us. Ages 11–16. Must book in advance.
                </p>

                <LeadForm />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="teens-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Built for Teen Athletes Ages 11–16
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                Structured Training Built for Teen Athletes
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  The teen years are a critical time for growth — physically,
                  mentally, and emotionally. Our No-Gi Jiu Jitsu program for
                  ages 11–16 provides a structured, focused training environment
                  where teenagers can develop real skills and genuine
                  confidence.
                </p>
                <p>
                  Each class is built around technique, live training, and
                  consistent progression. Teens learn to think under pressure,
                  problem-solve in real time, and build a work ethic that
                  carries over into school, sports, and everyday life.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_Teens jiu-jitsu.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="teens-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Real Results
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              What Teens Gain Through Training
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5"
              >
                <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-snug">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING SECTION ── */}
      <section
        className="bg-background py-16 px-4 border-t border-border"
        data-ocid="teens-training"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              How We Train
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              Progressive Coaching and Skill Development
            </h2>
            <p className="text-muted-foreground font-body text-base leading-relaxed max-w-2xl mx-auto">
              Our teen program is structured around progressive skill-building.
              Every class has a purpose — from drilling fundamental techniques
              to live sparring that develops real-world application.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-hero rounded-2xl overflow-hidden shadow-elevated mb-8"
          >
            <div className="h-1 bg-gradient-accent" />
            <div className="p-8 sm:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {trainingSteps.map(({ number, title, desc }, i) => (
                  <motion.div
                    key={number}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    className="text-center"
                  >
                    <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                      {number}
                    </span>
                    <h3 className="font-display font-bold text-base text-hero-foreground mb-3">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                      {desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-muted-foreground font-body text-base leading-relaxed text-center max-w-2xl mx-auto"
          >
            Teens who train consistently will see measurable improvement in
            their technique, fitness, and mental game. Whether your teen is
            brand new to martial arts or already an athlete, the program meets
            them where they are and pushes them forward.
          </motion.p>
        </div>
      </section>

      {/* ── WHO IT'S FOR SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="teens-who"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Everyone Welcome
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              WHO IT'S FOR
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col gap-4 shadow-card hover:shadow-elevated transition-smooth"
              >
                <span className="w-11 h-11 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="bg-hero py-20 px-4 text-center" data-ocid="teens-cta">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
              First Class Is Free
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
              Give Your Teen a Head Start
            </h2>
            <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
              Reserve a free class and see what structured Jiu Jitsu training
              can do for your teenager.
            </p>
            <p className="text-hero-foreground font-body text-sm mb-8 opacity-70">
              Must book in advance. Ages 11–16.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => router.navigate({ to: "/contact-us" })}
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
                data-ocid="teens-cta-button"
              >
                Book a Free Class
              </button>
              <a
                href="tel:+15416003371"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
                data-ocid="teens-cta-phone"
              >
                Call (541) 600-3371
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="teens-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <h2 className="font-display font-bold text-lg text-foreground mb-3">
              Teen Jiu Jitsu in Springfield, OR
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Superhero Jiu Jitsu Academy offers Teen Jiu Jitsu classes in
              Springfield, OR, proudly serving families in{" "}
              {serviceAreas.join(", ")}, and surrounding Lane County areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas, "Lane County"].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
